import { useMemo } from 'react'
import { useAuth } from '../../../context/AuthContext'

// Thin hook over the AuthContext `api()` helper. All endpoints live under
// /api/highlights/* in vader, which proxies via service binding to the
// tbtv-highlights worker. Future Watch/mobile/Roku/FireTV clients consume
// these same endpoints.
//
// The returned object is memoized on `api` so consumers can safely use it
// as a useEffect / useCallback dependency without re-firing on every
// render — previously every render produced a new object literal, which
// destabilized downstream deps and produced infinite refresh loops.
export function useHighlightsApi() {
  const { api } = useAuth()

  return useMemo(() => ({
    // -------- caption management --------
    enableCaptions(tbtvIds, language = 'en') {
      return api('/highlights/enable', {
        method: 'POST',
        body: JSON.stringify({ tbtv_ids: tbtvIds, language }),
      })
    },
    captionStatus(tbtvIds) {
      const qs = encodeURIComponent(tbtvIds.join(','))
      return api(`/highlights/status?tbtv_ids=${qs}`)
    },
    library({ status, q, limit = 50, offset = 0 } = {}) {
      // Don't default `status` — omitting it means "all statuses" server-side.
      // Don't send empty string params (URLSearchParams would include them).
      const params = new URLSearchParams({ limit, offset })
      if (status) params.set('status', status)
      if (q) params.set('q', q)
      return api(`/highlights/library?${params}`)
    },
    refresh(tbtvId) {
      return api(`/highlights/refresh/${tbtvId}`, { method: 'POST' })
    },
    transcript(tbtvId, { language = 'en' } = {}) {
      return api(`/highlights/transcript/${tbtvId}?language=${language}`)
    },
    deleteCaptions(tbtvId) {
      return api(`/highlights/${tbtvId}`, { method: 'DELETE' })
    },

    // -------- search --------
    // KM-side filters (momentType, source, minScore) mirror the Key Moments
    // tab's filter UX so a single set of controls feels consistent on both
    // surfaces. Each is no-op when null/empty.
    search(q, { limit = 20, mode = 'both', onlyInKeyMoments = false, momentType = null, source = null, minScore = null, expand = false, agentic = false } = {}) {
      const params = new URLSearchParams({ q, limit: String(limit), mode })
      if (onlyInKeyMoments) params.set('only_in_key_moments', 'true')
      if (momentType && momentType.length > 0) {
        params.set('moment_type', Array.isArray(momentType) ? momentType.join(',') : momentType)
      }
      if (source && source.length > 0) {
        params.set('source', Array.isArray(source) ? source.join(',') : source)
      }
      if (minScore != null) params.set('min_score', String(minScore))
      // Step 19: opt-in Claude query rewriter. Honored unless server-side
      // mode is 'off'. Ignored in 'always_on' (already running anyway).
      if (expand) params.set('expand', 'true')
      // Step 21 Phase C: opt-in agentic pipeline (Llama intent + Vector
      // semantic + Llama reranker). Returns same shape PLUS intent,
      // ranked_clips[], rerank_confidence, caught_by per video, pipeline
      // timings. First call on a new query is slow (~15-20s); cached
      // 24hr after that.
      if (agentic) params.set('agentic', 'true')
      return api(`/highlights/search?${params}`)
    },

    // -------- aliases (search expansion) --------
    // status filter: 'active' (default) | 'proposed' | 'rejected' | 'all'.
    aliases({ q, status } = {}) {
      const params = new URLSearchParams()
      if (q) params.set('q', q)
      if (status) params.set('status', status)
      const qs = params.toString()
      return api(`/highlights/aliases${qs ? '?' + qs : ''}`)
    },
    createAlias({ alias, canonical }) {
      return api('/highlights/aliases', {
        method: 'POST',
        body: JSON.stringify({ alias, canonical }),
      })
    },
    deleteAlias(alias) {
      return api(`/highlights/aliases/${encodeURIComponent(alias)}`, { method: 'DELETE' })
    },
    // Step 20: editor review — approve / reject / edit a Claude proposal.
    reviewAlias(alias, { canonical, status }) {
      return api(`/highlights/aliases/${encodeURIComponent(alias)}`, {
        method: 'PUT',
        body: JSON.stringify({ canonical, status }),
      })
    },
    // Step 20: trigger a Claude alias scan. tbtvIds optional — when omitted,
    // server auto-discovers indexed videos without a completed scan.
    scanAliases({ tbtvIds = null, force = false, retryErrored = false, limit = 200 } = {}) {
      const body = { force, limit }
      if (Array.isArray(tbtvIds) && tbtvIds.length > 0) body.tbtv_ids = tbtvIds
      if (retryErrored) body.retry_errored = true
      return api('/highlights/admin/aliases/scan', {
        method: 'POST',
        body: JSON.stringify(body),
      })
    },

    // -------- admin --------
    // One-shot backfill: populate phonetic_text for any cues missing it.
    // Call after deploying the phonetic FTS migration. Idempotent.
    reindexPhonetic({ limit } = {}) {
      const params = new URLSearchParams()
      if (limit) params.set('limit', limit)
      const qs = params.toString()
      return api(`/highlights/admin/reindex-phonetic${qs ? '?' + qs : ''}`, { method: 'POST' })
    },

    // -------- Mux Robots: insights + backfill --------
    keyMoments(tbtvId, { momentType = null } = {}) {
      const params = new URLSearchParams()
      if (momentType && momentType.length > 0) {
        params.set('moment_type', Array.isArray(momentType) ? momentType.join(',') : momentType)
      }
      const qs = params.toString()
      return api(`/highlights/key-moments/${tbtvId}${qs ? '?' + qs : ''}`)
    },
    keyMomentsList({ minScore = 0.7, limit = 50, language = 'en', momentType = null, source = null } = {}) {
      const params = new URLSearchParams({ limit: String(limit), language })
      if (minScore != null) params.set('min_score', String(minScore))
      if (momentType && momentType.length > 0) {
        params.set('moment_type', Array.isArray(momentType) ? momentType.join(',') : momentType)
      }
      if (source && source.length > 0) {
        params.set('source', Array.isArray(source) ? source.join(',') : source)
      }
      return api(`/highlights/key-moments?${params}`)
    },
    chapters(tbtvId) {
      return api(`/highlights/chapters/${tbtvId}`)
    },
    summary(tbtvId) {
      return api(`/highlights/summaries/${tbtvId}`)
    },
    // Auto-discover backfill (no tbtv_ids): server picks indexed videos
    // lacking a completed/in-flight job of this type.
    robotsBackfill({ jobType, force = false, limit = 25 } = {}) {
      const params = new URLSearchParams({ job_type: jobType, limit: String(limit) })
      if (force) params.set('force', 'true')
      return api(`/highlights/admin/robots-backfill?${params}`, { method: 'POST' })
    },

    // Apply Robots to a specific set of tbtv_ids — used by the multi-select
    // Apply Robots modal in the Key Moments tab.
    robotsApply({ jobType, tbtvIds, force = false } = {}) {
      const params = new URLSearchParams({ job_type: jobType, limit: String(Math.max(tbtvIds.length, 1)) })
      return api(`/highlights/admin/robots-backfill?${params}`, {
        method: 'POST',
        body: JSON.stringify({ tbtv_ids: tbtvIds, force }),
      })
    },

    // Clear AI output for the given videos. `kinds` is a subset of:
    //   mux_find_key_moments, mux_generate_chapters, mux_summarize,
    //   ai_find_key_moments, ai_generate_chapters, ai_summarize
    // When omitted, all 6 are cleared. Captions, cues, and the transcript
    // row are NEVER touched by this endpoint — use DELETE /:tbtv_id for
    // full purge including captions.
    clearAI({ tbtvIds, kinds = null, resetJobs = true } = {}) {
      const body = { tbtv_ids: tbtvIds, reset_jobs: resetJobs }
      if (kinds && kinds.length > 0) body.kinds = kinds
      return api('/highlights/admin/clear-ai', {
        method: 'POST',
        body: JSON.stringify(body),
      })
    },

    // -------- Step 26: editor reject + per-video drop list --------
    // Reject a moment. Server records the drop AND deletes the matching
    // key_moments row immediately so the editor sees the result. Future
    // TBTV-AI runs filter against the drop list, so the same bad moment
    // never re-appears unless the editor explicitly undoes the drop.
    //
    // Step 26.5: reason_category is a structured enum — only
    // 'hallucinated' and 'recap' feed Step 29's RAG injection.
    // 'other' requires a reason_note.
    dropMoment({
      tbtv_id, start_ms, end_ms, moment_type, title, source, detector,
      reason, reason_category, reason_note,
    } = {}) {
      return api('/highlights/admin/dropped-moments', {
        method: 'POST',
        body: JSON.stringify({
          tbtv_id, start_ms, end_ms, moment_type, title, source, detector,
          reason, reason_category, reason_note,
        }),
      })
    },
    // tbtv_id is optional — omit to fetch the whole catalog (capped at 5000).
    listDroppedMoments(tbtv_id = null, limit = null) {
      const params = new URLSearchParams()
      if (tbtv_id) params.set('tbtv_id', tbtv_id)
      if (limit) params.set('limit', String(limit))
      const qs = params.toString()
      return api(`/highlights/admin/dropped-moments${qs ? '?' + qs : ''}`)
    },
    droppedMomentsCounts() {
      return api('/highlights/admin/dropped-moments/counts')
    },
    undoDroppedMoment(id) {
      return api(`/highlights/admin/dropped-moments/${id}`, { method: 'DELETE' })
    },

    // -------- Step 27 Option B: editor approve --------
    // Positive label. Doubles as gold ground-truth for the eval harness
    // and as positive RAG exemplars for Step 29. No reason taxonomy —
    // approve = "this is a real reel-worthy moment, full stop." Optional
    // note for editor commentary.
    approveMoment({ tbtv_id, start_ms, end_ms, moment_type, title, source, detector, note } = {}) {
      return api('/highlights/admin/approved-moments', {
        method: 'POST',
        body: JSON.stringify({ tbtv_id, start_ms, end_ms, moment_type, title, source, detector, note }),
      })
    },
    // tbtv_id is optional — omit to fetch the whole catalog (capped at 5000).
    listApprovedMoments(tbtv_id = null, limit = null) {
      const params = new URLSearchParams()
      if (tbtv_id) params.set('tbtv_id', tbtv_id)
      if (limit) params.set('limit', String(limit))
      const qs = params.toString()
      return api(`/highlights/admin/approved-moments${qs ? '?' + qs : ''}`)
    },
    approvedMomentsCounts() {
      return api('/highlights/admin/approved-moments/counts')
    },
    // Returns [{tbtv_id, start_ms, title}] for every approval — used by the
    // UI to pre-seed the "Approved" badge state on page load so labels
    // persist across refreshes. Optional tbtv_id narrows to one video.
    approvedMomentsFingerprints(tbtv_id = null) {
      const path = tbtv_id
        ? `/highlights/admin/approved-moments/fingerprints?${new URLSearchParams({ tbtv_id })}`
        : '/highlights/admin/approved-moments/fingerprints'
      return api(path)
    },
    undoApprovedMoment(id) {
      return api(`/highlights/admin/approved-moments/${id}`, { method: 'DELETE' })
    },

    // -------- Step 27 UI: eval runner + history --------
    runEval({ gold_set = null, run_label = 'ui-triggered', include_keyword = false } = {}) {
      return api('/highlights/admin/eval/run', {
        method: 'POST',
        body: JSON.stringify({ gold_set, run_label, include_keyword }),
      })
    },
    evalLatest() {
      return api('/highlights/admin/eval/latest')
    },
    evalHistory({ gold_set = null, limit = 20 } = {}) {
      const params = new URLSearchParams({ limit: String(limit) })
      if (gold_set) params.set('gold_set', gold_set)
      return api(`/highlights/admin/eval/history?${params}`)
    },

    // Step 29: RAG preview — returns the exemplar block the LLM would see
    // on the next runLlmFindKeyMoments call for `tbtv_id`. Pass any
    // current-video id to get the same view the model gets (we exclude it
    // from the drop/approve pool to avoid self-feedback).
    ragPreview(tbtv_id = '__preview__') {
      const params = new URLSearchParams({ tbtv_id })
      return api(`/highlights/admin/rag/preview?${params}`)
    },

    // Step 28A Phase 2: entity-resolver backfill. POST per-tbtv_id triggers
    // a synchronous resolve of all cues for that video against teams-data.
    // GET returns the inventory of indexed videos with current mention counts
    // so the UI can drive a per-video loop.
    listEntityBackfill({ include_no_roster = false } = {}) {
      const qs = include_no_roster ? '?include_no_roster=true' : ''
      return api(`/highlights/admin/entity/backfill${qs}`)
    },
    runEntityBackfill(tbtv_id) {
      const params = new URLSearchParams({ tbtv_id })
      return api(`/highlights/admin/entity/backfill?${params}`, { method: 'POST' })
    },
    listEntityMentions(tbtv_id) {
      const params = new URLSearchParams({ tbtv_id })
      return api(`/highlights/admin/entity/mentions?${params}`)
    },
    rosterPromptPreview(tbtv_id) {
      const params = new URLSearchParams({ tbtv_id })
      return api(`/highlights/admin/entity/roster-preview?${params}`)
    },

    // Step 26.13: job recovery surface — list stuck (in-flight, > N min old)
    // and errored jobs, unstick by job_id. Re-running an unstuck job goes
    // through the existing robotsBackfill({ force: true }) path.
    listStuckJobs({ min_age_minutes = 3 } = {}) {
      const params = new URLSearchParams({ min_age_minutes: String(min_age_minutes) })
      return api(`/highlights/admin/robots-jobs/stuck?${params}`)
    },
    listErroredJobs({ days = 7 } = {}) {
      const params = new URLSearchParams({ days: String(days) })
      return api(`/highlights/admin/robots-jobs/errored?${params}`)
    },
    unstickJobs(job_ids) {
      return api('/highlights/admin/robots-jobs/unstick', {
        method: 'POST',
        body: JSON.stringify({ job_ids }),
      })
    },

    // -------- Settings --------
    listSettings() {
      return api('/highlights/settings')
    },
    setSetting(key, value) {
      return api(`/highlights/settings/${encodeURIComponent(key)}`, {
        method: 'PUT',
        body: JSON.stringify({ value: String(value) }),
      })
    },
    // Pricing + LLM model table + trailing 30-day ingest rate. Used by
    // SettingsEditor and RobotsApplyModal to render live cost previews.
    adminPricing() {
      return api('/highlights/admin/pricing')
    },

    // -------- clips --------
    // t = moment anchor (start_ms). endT = optional moment end_ms; when
    // supplied, backend window becomes [t - pre, endT + post] so the clip
    // honors the moment's full span instead of a fixed ±10s around t.
    clipUrl(tbtvId, { t, endT = null, pre = 10000, post = 10000 } = {}) {
      const params = new URLSearchParams({ t, pre, post })
      if (endT != null) params.set('end_t', String(endT))
      return api(`/highlights/clip-url/${tbtvId}?${params}`)
    },

    // -------- reels --------
    // mode: 'preview' (free, instant-clip chain) | 'encoded' (paid, Mux stitch)
    // description: optional long-form description (e.g. Mux Summarize output)
    // — written into vod_catalog.long_description when the reel asset is
    // registered in the catalog.
    createReel({ title, description, query, segments, pre_ms = 10000, post_ms = 10000, mode = 'preview' }) {
      return api('/highlights/reels', {
        method: 'POST',
        body: JSON.stringify({ title, description, query, segments, pre_ms, post_ms, mode }),
      })
    },
    listReels({ status } = {}) {
      const params = new URLSearchParams()
      if (status) params.set('status', status)
      const qs = params.toString()
      return api(`/highlights/reels${qs ? '?' + qs : ''}`)
    },
    getReel(reelId) {
      return api(`/highlights/reels/${reelId}`)
    },
    deleteReel(reelId) {
      return api(`/highlights/reels/${reelId}`, { method: 'DELETE' })
    },
    // Replace the reel's segment list. body.segments = [{ source_tbtv_id, start_ms, end_ms }, ...]
    // The worker re-resolves Mux IDs server-side and invalidates the edge cache,
    // so the same share URL serves the updated playlist on the next request.
    updateReelSegments(reelId, segments) {
      return api(`/highlights/reels/${reelId}/segments`, {
        method: 'PUT',
        body: JSON.stringify({ segments }),
      })
    },

    // ----- Step 32: publish to catalog + reel patch (thumbnail/relink) -----
    // Prefill bundle for the Publish-to-Catalog dialog: title, dominant-
    // source summaries, duration, thumbnail, suggested release date.
    reelPublishInfo(reelId) {
      return api(`/highlights/reels/${reelId}/publish-info`)
    },
    // Create a Highlights content-catalog row from a reel.
    publishReelToCatalog({ reel_id, title_override, short_description_override,
                           long_description_override, tags, release_date, status }) {
      return api('/vod/create-from-reel', {
        method: 'POST',
        body: JSON.stringify({
          reel_id, title_override, short_description_override,
          long_description_override, tags, release_date, status,
        }),
      })
    },
    // Update reel-level fields. Accepts { tbtv_id?, thumbnail_url? }.
    // Used by Publish-to-Catalog rollback, reel thumbnail picker, and the
    // CatalogEdit thumbnail reverse-sync path.
    patchReel(reelId, fields) {
      return api(`/highlights/reels/${reelId}`, {
        method: 'PATCH',
        body: JSON.stringify(fields),
      })
    },

    // ----- Step 33 continuation: reel transitions registry CRUD -----
    // GET all — used by SettingsEditor Transitions panel (shows every
    // entry including disabled + placeholder rows so editors can manage).
    listReelTransitionsAll() {
      return api('/highlights/admin/reel-transitions')
    },
    // GET usable subset — for ReelEditor per-gap picker (enabled +
    // populated only). Duplicate of what the stitcher reads.
    listReelTransitionsAvailable() {
      return api('/highlights/reel-transitions')
    },
    createReelTransition({ key, label, mux_playback_id, duration_seconds, kind, is_enabled, sort_order }) {
      return api('/highlights/admin/reel-transitions', {
        method: 'POST',
        body: JSON.stringify({ key, label, mux_playback_id, duration_seconds, kind, is_enabled, sort_order }),
      })
    },
    patchReelTransition(key, fields) {
      return api(`/highlights/admin/reel-transitions/${key}`, {
        method: 'PATCH',
        body: JSON.stringify(fields),
      })
    },
    deleteReelTransition(key) {
      return api(`/highlights/admin/reel-transitions/${key}`, { method: 'DELETE' })
    },

    // -------- sport profiles (Phase 1 read + Phase 4 write) --------
    // Backed by /profiles* routes in the highlights worker. Writes are
    // gated by strict server-side validation (bundle keys must reference
    // loaded code, moment_types / dedup_config / detect_hints must be
    // structurally correct — see routes/profiles.js).
    listProfiles({ enabledOnly = false } = {}) {
      const qs = enabledOnly ? '?enabled_only=true' : ''
      return api(`/highlights/profiles${qs}`)
    },
    getProfile(profileKey) {
      return api(`/highlights/profiles/${encodeURIComponent(profileKey)}`)
    },
    getProfileMomentTypes(profileKey) {
      return api(`/highlights/profiles/${encodeURIComponent(profileKey)}/moment-types`)
    },
    createProfile(body) {
      // body: { profile_key, display_name, prompt_bundle_key, vocab_key,
      //   moment_types (array), dedup_config?, roster_position_enum?,
      //   detect_hints?, is_enabled?, is_default?, sort_order? }
      return api('/highlights/profiles', {
        method: 'POST',
        body: JSON.stringify(body),
      })
    },
    patchProfile(profileKey, patch) {
      // Any subset of the create fields; profile_key rename rejected server-side.
      return api(`/highlights/profiles/${encodeURIComponent(profileKey)}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      })
    },
    // Phase 5.2: prompt overrides.
    // Returns { profile_key, prompt_bundle_key, prompts: [{ kind, bundled,
    // override, active, is_overridden, updated_at, updated_by }, ...] }
    getProfilePrompts(profileKey) {
      return api(`/highlights/profiles/${encodeURIComponent(profileKey)}/prompts`)
    },
    // Upsert an override. body = { content }. Server enforces non-empty
    // string + 32 KB byte cap.
    patchProfilePrompt(profileKey, promptKind, content) {
      return api(`/highlights/profiles/${encodeURIComponent(profileKey)}/prompts/${encodeURIComponent(promptKind)}`, {
        method: 'PATCH',
        body: JSON.stringify({ content }),
      })
    },
    // Clear the override → next resolveProfile call returns the bundled
    // .md file bytes.
    deleteProfilePrompt(profileKey, promptKind) {
      return api(`/highlights/profiles/${encodeURIComponent(profileKey)}/prompts/${encodeURIComponent(promptKind)}`, {
        method: 'DELETE',
      })
    },

    deleteProfile(profileKey) {
      // Server rejects when is_default=1 or any content.profile_key references it.
      return api(`/highlights/profiles/${encodeURIComponent(profileKey)}`, {
        method: 'DELETE',
      })
    },

    // -------- health (scaffold) --------
    health() {
      return api('/highlights/health')
    },
  }), [api])
}
